import clsx from "clsx";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Button,
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { CreateFirstProgram } from "../program/CreateFirstProgram";
import { UploadProfilePhotoDialog } from "../settings/dialogs/UploadProfilePhotoDialog";
import { polyfillCSS } from "../../utils/css";
import { JourneyAnchors, ProgramSort, ProgramStatus } from "../../constants";

import { CoachProgramCard, CoachProgramCardDisplay } from "./CoachProgramCard";
import { useProgramListFilterContext } from "../../contexts/ProgramListFilterContext";
import { useSearchParams } from "react-router-dom";
import TrackInfoTool from "../tools/TrackInfoTool";
import CoachProgramsService from "../../services/CoachProgramsService";
import { useDebounce } from "../../hooks/useDebounce";
import OptimisticUpdateContainer from "../loading/OptimisticUpdateContainer";
import { useCoachProgramsListScreenContext } from "../../contexts/CoachProgramsListScreenContext";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { LoadMoreButton } from "../button/LoadMoreButton";
import { PageSkeleton } from "../loading/PageSkeleton";
import { ProgramCardSkeleton } from "../loading/ProgramCardSkeleton";
import { EmptyResults } from "./EmptyResults";
import JourneyCustomTooltip from "../admin/journey/JourneyCustomTooltip";
import TutorialJourneysService from "../../services/TutorialJourneysService";
import { TutorialJourneyType } from "@growth-machine-llc/stridist-api-client";
import { useQuery } from "@tanstack/react-query";
import { TUTORIAL_JOURNEY_SINGLE_KEY } from "../../routes/admin/onboardingTemplate/JourneyTemplate";
import { fixLegacyContent } from "../editor/utils/common";
import NoteEditor from "../new-editor/NoteEditor";
import { useJoyrideSteps } from "../app/hooks/useJoyrideSteps";
import { getCookie } from "../program-component/utils";
import { useJourneyWithActivator } from "../app/hooks/useJourneyWithActivator";
import Joyride from "react-joyride";
import { JourneyAvailabilityContext } from "../../contexts/JourneyAvailabilityContext";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    paddingRight: polyfillCSS(
      `calc(${theme.spacing(5)} + var(--safe-area-inset-right)) !important`,
    ),
  },

  grid: {},
  list: {},

  clients: {
    display: "grid",
    gridGap: theme.spacing(3),
    gridTemplateColumns: "repeat(1, minmax(0, 1fr))",

    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gridGap: theme.spacing(3.5),
    },

    [theme.breakpoints.up("lg")]: {
      gridGap: theme.spacing(4),
    },

    "&$list": {
      [theme.breakpoints.up("sm")]: {
        display: "block",
      },
    },
  },

  filters: {
    marginBottom: theme.spacing(3),
  },

  card: {
    marginBottom: theme.spacing(3),
  },

  moreButton: {
    marginTop: theme.spacing(1.5),
  },

  empty: {
    marginTop: theme.spacing(15),
  },
}));

export type CoachProgramsListScreenProps = {
  status: ProgramStatus;
};

export const COACH_PROGRAMS_LIST_PAGE_SIZE = 12;
export const COACH_PROGRAMS_LIST_QUERY_KEY = "coach-programs";
const FETCH_NEXT_PAGE_WHEN_REMAINING = 4;

export function CoachProgramsListScreen(props: CoachProgramsListScreenProps) {
  const s = useStyles();
  const { status } = props;

  const { query, sortKey, folderId, display } = useProgramListFilterContext();

  const debouncedQuery = useDebounce(query, 500);

  const {
    data: programsData,
    ref: programElementRef,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: initialLoading,
    isFetching,
  } = useInfiniteScrollQuery({
    queryKey: [
      COACH_PROGRAMS_LIST_QUERY_KEY,
      { status, query: debouncedQuery, orderBy: sortKey, folderId },
    ],
    queryFn: ({ pageParam = 1 }) =>
      CoachProgramsService.getPrograms({
        status: [status],
        orderBy: sortKey ?? ProgramSort.DATE_ADDED_DESC,
        pageNumber: pageParam as number,
        clientsToTake: 5,
        pageSize: COACH_PROGRAMS_LIST_PAGE_SIZE,
        folderId: folderId ? folderId : null,
        ungrouped: folderId === "" ? true : null,
        query: debouncedQuery ? debouncedQuery : null,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.hasNextPage ? pages.length + 1 : undefined;
    },
  });

  const programs = React.useMemo(
    () => programsData?.pages?.flatMap((page) => page.items),
    [programsData?.pages],
  );

  const totalProgramsCount = React.useMemo(
    () => programsData?.pages?.[0]?.totalCount,
    [programsData?.pages],
  );

  const { setAvailability } = useContext(JourneyAvailabilityContext);

  useEffect(() => {
    if (programs && programs.length >= 2) {
      setAvailability({ enabled: true });
    } else {
      setAvailability({
        enabled: false,
        disabledReason:
          "At least two programs are required to start the journey",
      });
    }

    return () => {
      setAvailability({ enabled: true });
    };
  }, [programs, setAvailability]);

  const [searchParams, setSearchParams] = useSearchParams();
  const photoPrompt = searchParams.get("photoPrompt");

  const handleMoreClick = React.useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handlePhotoSaved = React.useCallback(() => {
    setSearchParams(undefined);
  }, [setSearchParams]);

  const handleSkipClick = React.useCallback(() => {
    setSearchParams(undefined);
  }, [setSearchParams]);

  const isLoading = initialLoading || (isFetching && !isFetchingNextPage);

  const showCreateFirstProgram =
    status === ProgramStatus.PUBLISHED && !debouncedQuery && !folderId;

  const top3 = programs?.slice(0, 3);
  const topCount = top3?.length;

  return (
    <>
      {!isLoading &&
        !!programs &&
        (totalProgramsCount === 0 ? (
          showCreateFirstProgram ? (
            <CreateFirstProgram />
          ) : (
            <EmptyResults
              className={s.empty}
              title={`No ${status.toLocaleLowerCase()} programs found`}
              description={`You don't have ${status.toLocaleLowerCase()} programs`}
            />
          )
        ) : (
          <Box
            className={clsx(
              s.clients,
              { [s.list]: display === CoachProgramCardDisplay.list },
              { [s.grid]: display === CoachProgramCardDisplay.grid },
            )}
          >
            <Box
              component="div"
              className={JourneyAnchors.PROGRAM_LIST_TOP3}
              sx={(theme) => ({
                display: display === "grid" ? "grid" : "block",
                ...(display === "grid" && {
                  gridColumn: topCount === 3 ? "1 / -1" : `span ${topCount}`,
                  gridGap: theme.spacing(3),
                  [theme.breakpoints.up("md")]: {
                    gridGap: theme.spacing(3.5),
                  },
                  [theme.breakpoints.up("lg")]: {
                    gridGap: theme.spacing(4),
                  },
                  gridTemplateColumns: {
                    md: `repeat(${topCount}, minmax(0, 1fr))`,
                  },
                }),
              })}
            >
              {top3?.map((program, index) => (
                <OptimisticUpdateContainer
                  key={program.id}
                  id={program.id}
                  className={clsx({
                    [JourneyAnchors.FIRST_PROGRAM]: index === 0,
                  })}
                >
                  <CoachProgramCard
                    display={display}
                    className={s.card}
                    program={program}
                  />
                </OptimisticUpdateContainer>
              ))}
            </Box>
            {programs?.slice(3).map((program, index, arr) => (
              <React.Fragment key={program.id}>
                <OptimisticUpdateContainer
                  id={program.id}
                  key={`optimistic-${program.id}`}
                  className={clsx({
                    [JourneyAnchors.FIRST_PROGRAM]: index === 0,
                  })}
                >
                  <CoachProgramCard
                    display={display}
                    className={`${s.card}`}
                    program={program}
                  />
                </OptimisticUpdateContainer>
                {index ===
                  (display === "list"
                    ? arr.length - FETCH_NEXT_PAGE_WHEN_REMAINING
                    : arr.length - 1) && (
                  <div
                    ref={programElementRef}
                    key={`program-${program.id}-element`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </Box>
        ))}

      {hasNextPage && (
        <LoadMoreButton
          onClick={handleMoreClick}
          fullWidth
          disabled={isLoading}
          loading={isFetchingNextPage}
        />
      )}

      {isLoading && (
        <PageSkeleton
          fullWidth
          ItemComponent={ProgramCardSkeleton}
          className={display === CoachProgramCardDisplay.grid && s.clients}
          itemProps={{ view: display }}
        />
      )}

      {photoPrompt && (
        <UploadProfilePhotoDialog
          open={Boolean(photoPrompt)}
          onSaved={handlePhotoSaved}
          onSkipClick={handleSkipClick}
        />
      )}
    </>
  );
}
